import { useState } from 'react'
import { Check, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { RezzAiIcon } from './RezzAiIcon'

interface Props {
  /** Collapsed call-to-action, e.g. "Paste a note to build this order". */
  cta: string
  /** Heading shown once expanded. */
  title: string
  placeholder: string
  rows?: number
  busy: boolean
  error: string | null
  /** Success line shown after a run completes; cleared by the parent. */
  summary: string | null
  actionLabel?: string
  onRun: (text: string) => void | Promise<void>
  onDismissError?: () => void
}

// Shared "paste some text and let RezzAI fill it in" affordance. Every AI entry
// point in the app uses this so the capability looks and behaves the same
// whether you're building a recipe, a catering order, or an invoice.
export function RezzAiPastePanel({
  cta, title, placeholder, rows = 4, busy, error, summary,
  actionLabel = 'Analyze & fill in', onRun, onDismissError,
}: Props) {
  const [open, setOpen] = useState(false)
  const [text, setText] = useState('')

  async function handleRun() {
    if (!text.trim()) return
    await onRun(text)
    setText('')
    setOpen(false)
  }

  return (
    <section className="rounded-xl border border-dashed border-brand-300 bg-brand-50/40 p-3">
      {!open ? (
        <button
          type="button"
          onClick={() => { setOpen(true); onDismissError?.() }}
          className="w-full flex items-center gap-2 text-sm font-medium text-brand-700 hover:text-brand-800"
        >
          <RezzAiIcon size={16} />
          {cta}
        </button>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm font-medium text-brand-700">
            <RezzAiIcon size={16} />
            {title}
          </div>
          <textarea
            autoFocus
            rows={rows}
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder={placeholder}
            className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400 resize-none bg-white"
          />
          <div className="flex items-center gap-2">
            <Button type="button" size="sm" onClick={handleRun} loading={busy} disabled={!text.trim()}>
              {busy ? <Loader2 size={13} className="animate-spin" /> : <RezzAiIcon size={13} mono />}
              {actionLabel}
            </Button>
            <button
              type="button"
              onClick={() => { setOpen(false); onDismissError?.() }}
              className="text-xs text-stone-400 hover:text-stone-600"
            >
              Cancel
            </button>
          </div>
          {error && <p className="text-xs text-danger">{error}</p>}
        </div>
      )}
      {!open && summary && (
        <p className="mt-1.5 text-xs text-success-dark flex items-center gap-1">
          <Check size={12} /> {summary}
        </p>
      )}
    </section>
  )
}
